import { create } from 'zustand';
import {
  AstuteOccupationLibraryListItem,
  integrationActions,
  IntegrationConnectionListItem,
} from './integration.actions';
import { SupportedIntegrationPlatform } from '../generated/gql-types';

export type IntegrationStoreStatus = 'idle' | 'loading' | 'ready' | 'error';

const inFlightRequests = new Map<string, Promise<void>>();
const inFlightOccupationLibraryRequests = new Map<
  string,
  Promise<AstuteOccupationLibraryListItem[]>
>();

interface IntegrationState {
  integrationConnections: IntegrationConnectionListItem[];
  integrationPlatformByIntegrationId: Record<
    string,
    SupportedIntegrationPlatform
  >;
  integrationStatus: IntegrationStoreStatus;
  loadedTenantId: string | null;
  astuteOccupationLibrariesByTenantId: Record<
    string,
    AstuteOccupationLibraryListItem[]
  >;
  /** Always hits the network, so newly created Astute libraries show up. */
  refreshAstuteOccupationLibraries: (
    tenantId: string,
  ) => Promise<AstuteOccupationLibraryListItem[]>;
  /** Reuses the cached list, since resolving a library name is read-only. */
  ensureAstuteOccupationLibraries: (
    tenantId: string,
  ) => Promise<AstuteOccupationLibraryListItem[]>;
  setIntegrationData: (
    tenantId: string,
    connections: IntegrationConnectionListItem[],
    platformByIntegrationId: Record<string, SupportedIntegrationPlatform>,
  ) => void;
  setIntegrationStatus: (integrationStatus: IntegrationStoreStatus) => void;
  clearIntegrationCache: () => void;
  fetchAndSetIntegrationsForTenant: (tenantId: string) => Promise<void>;
  ensureIntegrationConnections: (tenantId: string) => Promise<void>;
  getIntegrationsForTenant: () => IntegrationConnectionListItem[];
  getIntegrationByPlatform: (
    platform: SupportedIntegrationPlatform,
  ) => IntegrationConnectionListItem | null;
}

export const useIntegrationStore = create<IntegrationState>((set, get) => ({
  integrationConnections: [],
  integrationPlatformByIntegrationId: {},
  integrationStatus: 'idle',
  loadedTenantId: null,
  astuteOccupationLibrariesByTenantId: {},
  refreshAstuteOccupationLibraries: async (tenantId: string) => {
    const occupationLibraries =
      await integrationActions.fetchAstuteOccupationLibraries(tenantId);

    set((state) => ({
      astuteOccupationLibrariesByTenantId: {
        ...state.astuteOccupationLibrariesByTenantId,
        [tenantId]: occupationLibraries,
      },
    }));

    return occupationLibraries;
  },
  ensureAstuteOccupationLibraries: async (tenantId: string) => {
    const cachedOccupationLibraries =
      get().astuteOccupationLibrariesByTenantId[tenantId];
    if (cachedOccupationLibraries) {
      return cachedOccupationLibraries;
    }

    const inFlightRequest = inFlightOccupationLibraryRequests.get(tenantId);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const request = get()
      .refreshAstuteOccupationLibraries(tenantId)
      .finally(() => {
        inFlightOccupationLibraryRequests.delete(tenantId);
      });
    inFlightOccupationLibraryRequests.set(tenantId, request);

    return request;
  },
  setIntegrationData: (tenantId, connections, platformByIntegrationId) =>
    set({
      integrationConnections: connections,
      integrationPlatformByIntegrationId: platformByIntegrationId,
      integrationStatus: 'ready',
      loadedTenantId: tenantId,
    }),
  setIntegrationStatus: (integrationStatus) => set({ integrationStatus }),
  clearIntegrationCache: () =>
    set({
      integrationConnections: [],
      integrationPlatformByIntegrationId: {},
      integrationStatus: 'idle',
      loadedTenantId: null,
      astuteOccupationLibrariesByTenantId: {},
    }),
  fetchAndSetIntegrationsForTenant: async (tenantId: string) => {
    set({ integrationStatus: 'loading' });
    const result =
      await integrationActions.fetchIntegrationDataForTenant(tenantId);
    if (result) {
      set({
        integrationConnections: result.connections,
        integrationPlatformByIntegrationId: result.platformByIntegrationId,
        integrationStatus: 'ready',
        loadedTenantId: tenantId,
      });
    } else {
      set({ integrationStatus: 'error' });
    }
  },
  ensureIntegrationConnections: async (tenantId: string) => {
    const {
      loadedTenantId,
      integrationStatus,
      fetchAndSetIntegrationsForTenant,
    } = get();

    if (loadedTenantId === tenantId && integrationStatus === 'ready') {
      return;
    }

    if (inFlightRequests.has(tenantId)) {
      await inFlightRequests.get(tenantId)!;
      return;
    }

    const request = fetchAndSetIntegrationsForTenant(tenantId).finally(() => {
      inFlightRequests.delete(tenantId);
    });
    inFlightRequests.set(tenantId, request);
    await request;
  },
  getIntegrationsForTenant: () => get().integrationConnections,
  getIntegrationByPlatform: (platform: SupportedIntegrationPlatform) => {
    const { integrationConnections, integrationPlatformByIntegrationId } =
      get();
    return (
      integrationConnections.find(
        (integrationConnection) =>
          integrationPlatformByIntegrationId[
            integrationConnection.integrationId
          ] === platform,
      ) ?? null
    );
  },
}));
